import React from 'react'
import { Button, Card, Empty, Skeleton, Tag } from 'antd'
import type { ContinueReadingItem } from '../types/home'
import { applyLogoFallbackStyle, markLogoLikeImageIfNeeded } from '../utils/logoCover'

type ContinueReadingSectionProps = {
  items: ContinueReadingItem[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  usingFallback: boolean
  onLoadMore: () => void
  onBookClick: (item: ContinueReadingItem, position: number) => void
  onDiscoverMore: () => void
}

const formatProgressText = (value: number) => `已读 ${Math.max(0, Math.min(100, Number(value || 0))).toFixed(0)}%`
const formatRemainingText = (value: number) => value > 0 ? `预计剩余 ${value} 分钟` : '可立即开始阅读'

/**
 * 首页“延续阅读”展示区。
 */
const ContinueReadingSection: React.FC<ContinueReadingSectionProps> = ({
  items,
  loading,
  loadingMore,
  hasMore,
  usingFallback,
  onLoadMore,
  onBookClick,
  onDiscoverMore,
}) => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const loadingRef = React.useRef(false)

  React.useEffect(() => {
    loadingRef.current = loadingMore
  }, [loadingMore])

  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current
    if (!el || loadingRef.current || !hasMore) return
    const remain = el.scrollWidth - el.clientWidth - el.scrollLeft
    if (remain <= 160) {
      onLoadMore()
    }
  }, [hasMore, onLoadMore])

  const content = React.useMemo(() => {
    if (loading && items.length === 0) {
      return (
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="min-w-[220px] w-[220px] shrink-0 rounded-2xl border border-gray-100 bg-white p-4">
              <Skeleton.Image active style={{ width: '100%', height: 220, borderRadius: 16 }} />
              <Skeleton active paragraph={{ rows: 3 }} title={{ width: '90%' }} className="mt-4" />
            </div>
          ))}
        </div>
      )
    }
    if (items.length === 0) {
      return (
        <div className="rounded-2xl bg-gray-50 py-10">
          <Empty
            description="暂时还没有可展示的延续阅读书籍"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={onDiscoverMore}>
              去发现更多好书
            </Button>
          </Empty>
        </div>
      )
    }
    return (
      <div
        ref={scrollRef}
        className="overflow-x-auto pb-2"
        onScroll={handleScroll}
      >
        <div className="flex gap-4 min-w-full">
          {items.map((item, index) => (
            <Card
              key={`${item.book_id}-${index}`}
              hoverable
              className="min-w-[220px] w-[220px] shrink-0 border-none shadow-sm hover:shadow-theme-md rounded-2xl overflow-hidden"
              styles={{ body: { padding: 12 } }}
              cover={(
                <div className="relative w-full overflow-hidden bg-gray-100" style={{ aspectRatio: '3 / 4' }}>
                  <img
                    src={item.cover}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onLoad={(event) => {
                      const img = event.currentTarget
                      void markLogoLikeImageIfNeeded(img)
                    }}
                    onError={(event) => {
                      const img = event.currentTarget
                      if (img.dataset.fallbackApplied === '1') return
                      img.dataset.fallbackApplied = '1'
                      img.src = '/icons/My_logo.png'
                      applyLogoFallbackStyle(img)
                    }}
                  />
                  <div className="absolute left-2 top-2 flex items-center gap-2">
                    <Tag color="processing" style={{ marginInlineEnd: 0 }}>{formatProgressText(item.read_progress_percent)}</Tag>
                  </div>
                </div>
              )}
              onClick={() => onBookClick(item, index)}
            >
              <div className="text-gray-800 text-sm" style={{ fontWeight: 600, lineHeight: 1.5, marginBottom: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '3em' }}>
                {item.title}
              </div>
              <div className="text-xs text-gray-500 mb-2" style={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {item.author || '未知作者'}
              </div>
              <div className="text-xs text-primary-600 mb-1">{formatRemainingText(item.remaining_minutes)}</div>
              <div className="text-[11px] text-orange-500 mb-2">{item.hot_tag || '全站热门推荐'}</div>
              <div className="text-[12px] text-gray-500" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '3em' }}>
                {item.reason}
              </div>
            </Card>
          ))}
          {loadingMore ? (
            <div className="min-w-[220px] w-[220px] shrink-0 rounded-2xl border border-dashed border-primary-200 bg-primary-50/40 p-4 flex items-center justify-center text-sm text-primary-600">
              正在加载更多...
            </div>
          ) : null}
        </div>
      </div>
    )
  }, [handleScroll, items, loading, loadingMore, onBookClick, onDiscoverMore])

  return (
    <Card
      title={<span className="text-lg font-bold text-gray-800">延续阅读</span>}
      extra={(
        <div className="flex items-center gap-2">
          {usingFallback ? <Tag color="gold" style={{ marginInlineEnd: 0 }}>热度兜底</Tag> : null}
          <Button size="small" onClick={hasMore ? onLoadMore : onDiscoverMore} loading={loadingMore}>
            {hasMore ? '更多' : '去发现'}
          </Button>
        </div>
      )}
      className="theme-card border-none mb-6"
      styles={{ header: { borderBottom: '1px solid var(--color-primary-50)' } }}
    >
      {content}
    </Card>
  )
}

export default ContinueReadingSection
